import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, RefreshCw, Trash2, GitBranch, CheckCircle, Clock,
  XCircle, Loader2, MessageSquare, FileCode,
  AlertTriangle, X, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatRelativeTime, formatNumber } from '../lib/utils';
import { SEO } from '../components/SEO';
import { API_URL } from '../lib/config';
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('cp_token')}`, 'Content-Type': 'application/json' };
}

interface Repo {
  _id: string;
  name: string;
  url: string;
  branch: string;
  provider: string;
  indexingStatus: 'pending' | 'in_progress' | 'completed' | 'failed';
  lastIndexedAt?: string;
  fileCount?: number;
  errorMessage?: string;
}

const STATUS = {
  pending:     { icon: Clock,       color: 'text-amber-400',   ring: 'ring-amber-400/20',   label: 'Pending' },
  in_progress: { icon: Loader2,     color: 'text-blue-400',    ring: 'ring-blue-400/20',    label: 'Indexing' },
  completed:   { icon: CheckCircle, color: 'text-emerald-400', ring: 'ring-emerald-400/20', label: 'Indexed' },
  failed:      { icon: XCircle,     color: 'text-red-400',     ring: 'ring-red-400/20',     label: 'Failed' },
};

/* ─── Add repository modal ──────────────────────────────────── */

interface AddRepoModalProps {
  onClose: () => void;
  onAdd: (repo: Repo) => void;
}

function AddRepoModal({ onClose, onAdd }: AddRepoModalProps) {
  const [form, setForm] = useState({ name: '', url: '', branch: 'main', provider: 'github' });
  const [adding, setAdding] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAdding(true);
    try {
      const res = await fetch(`${API_URL}/repositories`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error?.message || 'Failed to add repository');
      onAdd(data.data);
      toast.success('Repository added — indexing started');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 backdrop-blur-sm" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div className="relative w-full max-w-[420px] card p-6 shadow-surface-lg animate-slide-up">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-t1">Connect repository</h2>
            <p className="text-xs text-t3 mt-0.5">Add a repository to index and analyze</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-t3 hover:text-t1 transition-colors"
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-md)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs text-t2 font-medium block mb-2">Git provider</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'github',    label: 'GitHub' },
                { value: 'gitlab',    label: 'GitLab' },
                { value: 'bitbucket', label: 'Bitbucket' },
              ].map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, provider: p.value }))}
                  className="py-2 rounded-lg border text-xs font-medium transition-all"
                  style={form.provider === p.value
                    ? { borderColor: 'var(--t1)', backgroundColor: 'var(--overlay-sm)', color: 'var(--t1)' }
                    : { borderColor: 'var(--b-default)', color: 'var(--t3)', backgroundColor: 'transparent' }
                  }
                  onMouseEnter={e => {
                    if (form.provider !== p.value) {
                      e.currentTarget.style.borderColor = 'var(--b-strong)';
                      e.currentTarget.style.color = 'var(--t1)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (form.provider !== p.value) {
                      e.currentTarget.style.borderColor = 'var(--b-default)';
                      e.currentTarget.style.color = 'var(--t3)';
                    }
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-t2 font-medium block mb-1.5">Repository name</label>
            <input type="text" placeholder="e.g. acme/payments-service" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="input-base" />
          </div>
          <div>
            <label className="text-xs text-t2 font-medium block mb-1.5">Repository URL</label>
            <input type="url" placeholder="https://github.com/org/repository" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} required className="input-base" />
          </div>
          <div>
            <label className="text-xs text-t2 font-medium block mb-1.5">Branch</label>
            <input type="text" placeholder="main" value={form.branch} onChange={e => setForm(f => ({ ...f, branch: e.target.value }))} className="input-base" />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center py-2.5">Cancel</button>
            <button type="submit" disabled={adding} className="btn-primary flex-1 justify-center py-2.5 disabled:opacity-50">
              {adding ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Adding…</> : 'Connect & Index'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ─── Repository card ───────────────────────────────────────── */

interface RepoCardProps {
  repo: Repo;
  onReIndex: (id: string) => void;
  onDelete: (id: string) => void;
}

function RepoCard({ repo, onReIndex, onDelete }: RepoCardProps) {
  const s = STATUS[repo.indexingStatus];
  const StatusIcon = s.icon;

  return (
    <div
      className="card p-5 transition-all group"
      onMouseEnter={e => (e.currentTarget.style.borderColor = 'var(--b-strong)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = 'var(--b-subtle)')}
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl border flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--s4)', borderColor: 'var(--b-default)' }}>
          <GitBranch className="w-5 h-5 text-t3" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-t1 truncate">{repo.name}</h3>
                <span className="text-[10px] px-1.5 py-0.5 rounded text-t3 capitalize flex-shrink-0" style={{ backgroundColor: 'var(--s4)' }}>
                  {repo.provider}
                </span>
              </div>
              <a href={repo.url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-t4 hover:text-t2 transition-colors flex items-center gap-1 mt-0.5">
                {repo.url.replace('https://', '')}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <div className={`flex items-center gap-1.5 flex-shrink-0 px-2 py-1 rounded-full ring-1 ${s.ring}`}>
              <StatusIcon className={`w-3 h-3 ${s.color} ${repo.indexingStatus === 'in_progress' ? 'animate-spin' : ''}`} />
              <span className={`text-[10px] font-medium ${s.color}`}>{s.label}</span>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1 text-xs text-t3">
              <GitBranch className="w-3 h-3" />
              {repo.branch}
            </span>
            {repo.fileCount !== undefined && (
              <span className="flex items-center gap-1 text-xs text-t3">
                <FileCode className="w-3 h-3" />
                {formatNumber(repo.fileCount)} files
              </span>
            )}
            {repo.lastIndexedAt && (
              <span className="flex items-center gap-1 text-xs text-t3">
                <Clock className="w-3 h-3" />
                {formatRelativeTime(repo.lastIndexedAt)}
              </span>
            )}
          </div>

          {repo.errorMessage && (
            <div className="flex items-start gap-2 mt-2 p-2 rounded-lg"
              style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.20)' }}>
              <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-xs text-red-400/80">{repo.errorMessage}</span>
            </div>
          )}

          <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <Link to={`/conversations?repo=${repo._id}`}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-t1 transition-colors"
              style={{ border: '1px solid var(--b-default)', backgroundColor: 'transparent' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--overlay-sm)')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <MessageSquare className="w-3 h-3" />
              Ask AI
            </Link>
            <button onClick={() => onReIndex(repo._id)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-t2 transition-colors"
              style={{ border: '1px solid var(--b-default)', backgroundColor: 'transparent' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'var(--b-strong)';
                e.currentTarget.style.color = 'var(--t1)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'var(--b-default)';
                e.currentTarget.style.color = 'var(--t2)';
              }}
            >
              <RefreshCw className="w-3 h-3" />
              Re-index
            </button>
            <button onClick={() => onDelete(repo._id)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs text-t4 transition-colors ml-auto"
              style={{ border: '1px solid transparent', backgroundColor: 'transparent' }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = 'rgba(239,68,68,0.30)';
                e.currentTarget.style.color = '#f87171';
                e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.06)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'transparent';
                e.currentTarget.style.color = 'var(--t4)';
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2 className="w-3 h-3" />
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────── */

export function RepositoriesPage() {
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState<'all' | 'indexed' | 'pending' | 'failed'>('all');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/repositories`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setRepos(data.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const inProgress = repos.some(r => r.indexingStatus === 'in_progress' || r.indexingStatus === 'pending');
    if (!inProgress) return;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [repos, load]);

  async function reIndex(id: string) {
    try {
      const res = await fetch(`${API_URL}/repositories/${id}/index`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!data.success) throw new Error(data.error?.message);
      setRepos(prev => prev.map(r => r._id === id ? { ...r, indexingStatus: 'pending' } : r));
      toast.success('Re-indexing queued');
    } catch (err: any) { toast.error(err.message); }
  }

  async function deleteRepo(id: string) {
    if (!confirm('Remove this repository? All indexed embeddings will be deleted.')) return;
    try {
      await fetch(`${API_URL}/repositories/${id}`, { method: 'DELETE', headers: authHeaders() });
      setRepos(prev => prev.filter(r => r._id !== id));
      toast.success('Repository removed');
    } catch { toast.error('Failed to remove repository'); }
  }

  const filtered = repos.filter(r => {
    if (filter === 'indexed') return r.indexingStatus === 'completed';
    if (filter === 'pending') return r.indexingStatus === 'in_progress' || r.indexingStatus === 'pending';
    if (filter === 'failed')  return r.indexingStatus === 'failed';
    return true;
  });

  const stats = {
    total:   repos.length,
    indexed: repos.filter(r => r.indexingStatus === 'completed').length,
    files:   repos.reduce((s, r) => s + (r.fileCount || 0), 0),
  };

  return (
    <div className="p-6 max-w-[1200px] mx-auto space-y-6">
      <SEO title="Repositories" description="Manage and monitor your connected repositories. View indexing status, file counts, and jump into AI-powered codebase conversations." noIndex />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-t1 tracking-tight">Repository Intelligence</h1>
          <p className="text-sm text-t3 mt-0.5">
            {stats.indexed} of {stats.total} repositories indexed
            {stats.files > 0 && ` · ${formatNumber(stats.files)} files processed`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="btn-ghost">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => setShowAdd(true)} className="btn-primary">
            <Plus className="w-4 h-4" />
            Connect Repository
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { icon: GitBranch,   label: 'Total Repositories', value: stats.total || '—',                                      color: 'text-t3' },
          { icon: CheckCircle, label: 'Indexed',            value: stats.indexed || '—',                                    color: 'text-emerald-400' },
          { icon: FileCode,    label: 'Files Processed',    value: stats.files > 0 ? formatNumber(stats.files) : '—',       color: 'text-blue-400' },
        ].map(s => (
          <div key={s.label} className="card p-4 flex items-center gap-4">
            <div className="w-9 h-9 rounded-xl border flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--s4)', borderColor: 'var(--b-subtle)' }}>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div>
              <div className="text-xl font-bold text-t1 tracking-tight">{s.value}</div>
              <div className="text-xs text-t3">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-1">
        {([
          { key: 'all',     label: 'All',     count: repos.length },
          { key: 'indexed', label: 'Indexed', count: repos.filter(r => r.indexingStatus === 'completed').length },
          { key: 'pending', label: 'Syncing', count: repos.filter(r => ['pending','in_progress'].includes(r.indexingStatus)).length },
          { key: 'failed',  label: 'Failed',  count: repos.filter(r => r.indexingStatus === 'failed').length },
        ] as const).map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={filter === f.key
              ? { backgroundColor: 'var(--s4)', color: 'var(--t1)' }
              : { color: 'var(--t3)', backgroundColor: 'transparent' }
            }
            onMouseEnter={e => {
              if (filter !== f.key) e.currentTarget.style.color = 'var(--t1)';
            }}
            onMouseLeave={e => {
              if (filter !== f.key) e.currentTarget.style.color = 'var(--t3)';
            }}
          >
            {f.label}
            {f.count > 0 && (
              <span
                className="px-1.5 py-0.5 rounded-full text-[10px]"
                style={filter === f.key
                  ? { backgroundColor: 'var(--s5)', color: 'var(--t1)' }
                  : { backgroundColor: 'var(--s4)', color: 'var(--t3)' }
                }
              >
                {f.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Repository list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-t4" />
        </div>
      ) : filtered.length === 0 && repos.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-14 h-14 rounded-2xl border flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: 'var(--s1)', borderColor: 'var(--b-subtle)' }}>
            <GitBranch className="w-7 h-7 text-t4" />
          </div>
          <h3 className="text-base font-semibold text-t1 mb-2">No repositories connected</h3>
          <p className="text-sm text-t3 max-w-xs mx-auto leading-relaxed mb-6">
            Connect your first repository to start indexing and enabling AI-powered codebase intelligence.
          </p>
          <button onClick={() => setShowAdd(true)} className="btn-primary mx-auto">
            <Plus className="w-4 h-4" />
            Connect repository
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-t3">
          <p className="text-sm">No repositories match this filter</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(repo => (
            <RepoCard key={repo._id} repo={repo} onReIndex={reIndex} onDelete={deleteRepo} />
          ))}
        </div>
      )}

      {showAdd && (
        <AddRepoModal
          onClose={() => setShowAdd(false)}
          onAdd={repo => { setRepos(prev => [repo, ...prev]); setShowAdd(false); }}
        />
      )}
    </div>
  );
}
